import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, FileText, Globe, LayoutTemplate, Lock, Sparkles, SquareDashed } from "lucide-react";
import { COMMON_LANGUAGES, type MenuSummaryDto, type RestaurantDto } from "@dishfolio/contracts";
import { THEMES } from "@dishfolio/themes";
import { Badge, Button, Card, Field, Input, NativeSelect, cn } from "@dishfolio/ui";
import { api, setApiOrg } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { Logo } from "../components/Logo.tsx";
import { notifyError } from "../components/Upgrade.tsx";
import { uploadImage } from "../components/MediaPicker.tsx";

export const CURRENCIES = ["EUR", "BAM", "USD", "GBP", "CHF", "HRK", "RSD", "PLN", "CZK", "HUF", "TRY", "DKK", "SEK", "NOK", "JPY", "AUD", "CAD"];

export function timeZones(): string[] {
  try {
    return (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Europe/Sarajevo", "Europe/Berlin", "Europe/London", "America/New_York"];
  }
}

export function RestaurantFields({ value, onChange }: { value: RestaurantDraft; onChange: (v: RestaurantDraft) => void }) {
  const set = (patch: Partial<RestaurantDraft>) => onChange({ ...value, ...patch });
  return (
    <div className="grid gap-4">
      <Field label="Restaurant name" htmlFor="rname">
        <Input id="rname" required value={value.name} onChange={(e) => set({ name: e.target.value })} placeholder="Trattoria Uno" />
      </Field>
      <Field label="Address" htmlFor="raddr" hint="Shown on your menu (optional).">
        <Input id="raddr" value={value.address} onChange={(e) => set({ address: e.target.value })} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Menu language" htmlFor="rlang">
          <NativeSelect id="rlang" value={value.defaultLanguage} onChange={(e) => set({ defaultLanguage: e.target.value })}>
            {COMMON_LANGUAGES.map((l) => (
              <option key={l.tag} value={l.tag}>
                {l.name}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Currency" htmlFor="rcur">
          <NativeSelect id="rcur" value={value.currency} onChange={(e) => set({ currency: e.target.value })}>
            {CURRENCIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Time zone" htmlFor="rtz">
          <NativeSelect id="rtz" value={value.timezone} onChange={(e) => set({ timezone: e.target.value })}>
            {/* Keep the current value selectable even if the browser's list omits it (e.g. "UTC"). */}
            {[...new Set([value.timezone, ...timeZones()])].map((t) => (
              <option key={t}>{t}</option>
            ))}
          </NativeSelect>
        </Field>
      </div>
    </div>
  );
}

export type RestaurantDraft = { name: string; address: string; defaultLanguage: string; currency: string; timezone: string };

type StartChoice = "theme" | "blank" | "sample" | "website" | "pdf";

const STEPS = ["Organization", "Restaurant", "Start"];

export function OnboardingPage() {
  const qc = useQueryClient();
  const { me } = useOrg();
  const hasOrg = me.organizations.length > 0;
  const [step, setStep] = useState(0);
  const [orgName, setOrgName] = useState(hasOrg ? "" : `${me.user.name.split(" ")[0]}'s restaurants`);
  const browserLang = (navigator.language || "en").split("-")[0]!;
  const [restaurant, setRestaurant] = useState<RestaurantDraft>({
    name: "",
    address: "",
    defaultLanguage: COMMON_LANGUAGES.some((l) => l.tag === browserLang) ? browserLang : "en",
    currency: "EUR",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  });
  const [logo, setLogo] = useState<File | null>(null);
  const [created, setCreated] = useState<RestaurantDto | null>(null);
  const [choice, setChoice] = useState<StartChoice>("theme");
  const [themeId, setThemeId] = useState("basic");
  const [loading, setLoading] = useState(false);

  const createOrg = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const org = await api.post<{ id: string }>("/organizations", { name: orgName });
      setApiOrg(org.id);
      localStorage.setItem("dishfolio.org", org.id);
      setStep(1);
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  };

  const createRestaurant = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      let logoAssetId: string | null = null;
      if (logo) logoAssetId = (await uploadImage(logo, "logo")).id;
      const res = await api.post<{ restaurant: RestaurantDto }>("/restaurants", { ...restaurant, logoAssetId });
      setCreated(res.restaurant);
      setStep(2);
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    if (!created) return;
    if (choice === "website" || choice === "pdf") {
      await qc.invalidateQueries();
      window.location.href = `/imports/new?type=${choice}&restaurantId=${created.id}`;
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<{ menu: MenuSummaryDto }>("/menus", {
        restaurantId: created.id,
        name: "Menu",
        themeId: choice === "theme" ? themeId : "basic",
        template: choice,
      });
      await qc.invalidateQueries();
      window.location.href = `/menus/${res.menu.id}`;
    } catch (err) {
      notifyError(err);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-full bg-gradient-to-b from-accent/60 to-background px-4 py-10">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 flex items-center justify-between">
          <Logo />
          <ol className="flex items-center gap-2 text-sm">
            {STEPS.map((s, i) => (
              <li key={s} className={cn("flex items-center gap-1.5", i === step ? "font-medium text-foreground" : "text-muted-foreground")}>
                <span className={cn("flex size-6 items-center justify-center rounded-full border text-xs", i < step && "border-primary bg-primary text-primary-foreground", i === step && "border-primary text-primary")}>
                  {i < step ? <Check className="size-3.5" /> : i + 1}
                </span>
                <span className="hidden sm:inline">{s}</span>
              </li>
            ))}
          </ol>
        </div>

        {step === 0 ? (
          <Card className="gap-5 p-6">
            <div>
              <h1 className="font-display text-2xl font-semibold">Welcome to Dishfolio{hasOrg ? "" : `, ${me.user.name.split(" ")[0]}`}</h1>
              <p className="mt-1 text-muted-foreground">An organization holds your restaurants, team and billing.</p>
            </div>
            <form onSubmit={createOrg} className="grid gap-4">
              <Field label="Organization name" htmlFor="org">
                <Input id="org" required value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Uno Hospitality Group" />
              </Field>
              <Button type="submit" loading={loading} className="justify-self-start">
                Continue <ArrowRight />
              </Button>
            </form>
          </Card>
        ) : null}

        {step === 1 ? (
          <Card className="gap-5 p-6">
            <div>
              <h1 className="font-display text-2xl font-semibold">Tell us about your restaurant</h1>
              <p className="mt-1 text-muted-foreground">You can change all of this later.</p>
            </div>
            <form onSubmit={createRestaurant} className="grid gap-4">
              <RestaurantFields value={restaurant} onChange={setRestaurant} />
              <Field label="Logo" hint="PNG or SVG-like images with transparency look best.">
                <Input type="file" accept="image/*" onChange={(e) => setLogo(e.target.files?.[0] ?? null)} />
              </Field>
              <Button type="submit" loading={loading} className="justify-self-start">
                Continue <ArrowRight />
              </Button>
            </form>
          </Card>
        ) : null}

        {step === 2 ? (
          <Card className="gap-5 p-6">
            <div>
              <h1 className="font-display text-2xl font-semibold">How do you want to start?</h1>
              <p className="mt-1 text-muted-foreground">Pick a starting point for {created?.name}’s first menu.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {(
                [
                  { key: "theme", icon: <LayoutTemplate />, title: "Start from a theme", desc: "A designed layout ready for your dishes." },
                  { key: "sample", icon: <Sparkles />, title: "Sample menu", desc: "A theme with example categories and dishes to edit." },
                  { key: "blank", icon: <SquareDashed />, title: "Blank menu", desc: "Just a title and your categories." },
                  { key: "website", icon: <Globe />, title: "Import from website", desc: "We read your site’s menu, colors and fonts.", pro: true },
                  { key: "pdf", icon: <FileText />, title: "Import a PDF", desc: "Upload your printed menu — text or scanned.", pro: true },
                ] as Array<{ key: StartChoice; icon: React.ReactNode; title: string; desc: string; pro?: boolean }>
              ).map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setChoice(o.key)}
                  className={cn("flex cursor-pointer items-start gap-3 rounded-xl border bg-card p-4 text-left transition hover:border-primary/50 [&_svg]:size-5", choice === o.key && "border-primary ring-2 ring-primary/20")}
                >
                  <span className="mt-0.5 text-primary">{o.icon}</span>
                  <span className="flex-1">
                    <span className="flex items-center gap-2 font-medium">
                      {o.title}
                      {o.pro ? (
                        <Badge variant="secondary">
                          <Lock /> Pro
                        </Badge>
                      ) : null}
                    </span>
                    <span className="mt-0.5 block text-sm text-muted-foreground">{o.desc}</span>
                  </span>
                </button>
              ))}
            </div>
            {choice === "theme" ? (
              <div className="grid gap-2">
                <div className="text-sm font-medium">Theme</div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {THEMES.map((t) => {
                    const locked = t.isPremium;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        disabled={locked}
                        onClick={() => setThemeId(t.key)}
                        className={cn("flex flex-col gap-2 rounded-lg border p-2 text-left disabled:cursor-not-allowed disabled:opacity-50", themeId === t.key && "border-primary ring-2 ring-primary/20")}
                      >
                        <div className="flex h-12 overflow-hidden rounded">
                          {t.preview.swatches.map((c) => (
                            <span key={c} className="flex-1" style={{ background: c }} />
                          ))}
                        </div>
                        <span className="flex items-center justify-between text-xs font-medium">
                          {t.name} {locked ? <Lock className="size-3" /> : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">The Free plan includes the Basic theme. Upgrade any time to unlock all themes.</p>
              </div>
            ) : null}
            {(choice === "website" || choice === "pdf") ? (
              <p className="rounded-md bg-accent p-3 text-sm">Imports are part of the Pro plan. You’ll be able to upgrade on the next screen; nothing is charged until you confirm a plan.</p>
            ) : null}
            <Button onClick={finish} loading={loading} className="justify-self-start">
              Create my menu <ArrowRight />
            </Button>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
